import React, {FC, useCallback, useRef} from 'react';
import {SortFilterContainer} from "../../../components/common/reports/sortFilter/SortFilterContainer";
import {SortFilterItemContainer} from "../../../components/common/reports/sortFilter/SortFilterItemContainer";
import {TextField, SelectChangeEvent} from "@mui/material";
import SimpleSelect from "../../../components/SimpleSelect";
import {AccountType, DateFormats} from "../../../components/constants/securspace-constants";
import {LabelValue} from "../../../types/common/LabelValue";
import DatePicker from "../../../components/common/inputs/DatePicker/DatePicker";
import moment, {Moment} from "moment";
import SortBy, {SortByValue} from "./SortBy";
import {SortDirType} from "../../../types/common/SortDirType";
import {AdminCompanyNamesAutocomplete} from "../../../components/admin/autocompletes/AdminCompanyNamesAutocomplete";
import {AutocompleteOption} from "../../../components/SortFilter/interfaces";

type ActivatedValues = 'active' | 'inactive' | '';

/**
 * The filter values for the Admin Accounts Report Sort Filter
 * @property {string} type - Current selected {@link AccountType} value
 * @property {string} accountId - The account ID used for filtering (from CompanyNameOption.id)
 */
export interface IFilterValues {
  companyName: string;
  accountId: string;
  activated: ActivatedValues;
  type: string;
  email: string;
  username: string;
  creationDate: string;
  sortBy: string;
  sortDir: SortDirType;
}

interface ISortFilterProps {
  filterValues: IFilterValues
  onFilterChange: (filters: ISortFilterProps['filterValues']) => void;
}

const userTypeOptions: LabelValue[] = [
  {value: '', label: 'All'},
  {value: AccountType.ADMIN, label: 'Admin'},
  {value: AccountType.BUYER, label: 'Buyer'},
  {value: AccountType.SUPPLIER, label: 'Supplier'},
];

//TODO: inactive should include null value as well (backend change may be needed)
const activatedStatusOptions: LabelValue[] = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

/**
 * Sort Filter component for the Admin Accounts Report
 * @return FC<ISortFilterProps>
 *
 * @see {IFilterValues}
 */
const SortFilter: FC<ISortFilterProps> = (props: ISortFilterProps) => {
  const {filterValues, onFilterChange} = props;

  // Refs for stable company-name change callback (avoids re-creating on every render)
  const filterValuesRef = useRef(filterValues);
  filterValuesRef.current = filterValues;
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  // Resolve account type to the supported union for the autocomplete
  const resolvedAccountType = filterValues.type === AccountType.SUPPLIER || filterValues.type === AccountType.BUYER
    ? filterValues.type as typeof AccountType.SUPPLIER | typeof AccountType.BUYER
    : undefined;

  // Stable callback for company name selection
  const handleCompanyNameChange = useCallback((_event: React.SyntheticEvent, value: AutocompleteOption | null) => {
    onFilterChangeRef.current({
      ...filterValuesRef.current,
      companyName: value?.label || '',
      accountId: value?.id || ''
    });
  }, []);

  const handleUserTypeChange = (event: SelectChangeEvent) => {
    onFilterChange({...filterValues, type: event.target.value});
  };

  const handleActivatedChange = (event: SelectChangeEvent) => {
    onFilterChange({...filterValues, activated: event.target.value as ActivatedValues});
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onFilterChange({...filterValues, email: value});
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onFilterChange({...filterValues, username: value});
  };

  const handleCreationDateChange = (date: Moment | null) => {
    const formattedDate = date ? date.startOf('day').format(DateFormats.DAY) : '';
    onFilterChange({...filterValues, creationDate: formattedDate});
  };

  return <SortFilterContainer flexDirection="row" spacing={2} wrap={"wrap"}>
    <SortFilterItemContainer xs={12} sm={6} md={4} lg={4}>
      <AdminCompanyNamesAutocomplete
        id="company-name-autocomplete"
        accountType={resolvedAccountType}
        value={filterValues.accountId}
        onChange={handleCompanyNameChange}
      />
    </SortFilterItemContainer>
    <SortFilterItemContainer xs={12} sm={6} md={4} lg={4}>
      <SimpleSelect
        name="Type"
        value={filterValues.type}
        label="User Type"
        placeholder="Select User Type"
        displayEmpty
        options={userTypeOptions}
        onChange={handleUserTypeChange}
      />
    </SortFilterItemContainer>
    <SortFilterItemContainer xs={12} sm={6} md={4} lg={4}>
      <SimpleSelect
        name="activated"
        value={filterValues.activated}
        label="Account Status"
        placeholder="Select Status"
        displayEmpty
        options={activatedStatusOptions}
        onChange={handleActivatedChange}
      />
    </SortFilterItemContainer>
    <SortFilterItemContainer sm={6} md={4}>
      <TextField
        name="email"
        label="Company Email"
        variant="standard"
        fullWidth
        value={filterValues.email}
        onChange={handleEmailChange}
      />
    </SortFilterItemContainer>
    <SortFilterItemContainer sm={12} md={4}>
      <TextField
        name="username"
        label="Owner Username"
        variant="standard"
        fullWidth
        value={filterValues.username}
        onChange={handleUsernameChange}
      />
    </SortFilterItemContainer>
    <SortFilterItemContainer sm={12} md={4}>
      <DatePicker
        label="Created On or After"
        value={filterValues.creationDate || null}
        onChange={handleCreationDateChange}
        disableFuture={true}
        minDate={moment('01/01/1901', DateFormats.DAY)}
      />
    </SortFilterItemContainer>
    <SortFilterItemContainer sm={12} md={4}>
      <SortBy
        sortByValue={filterValues.sortBy as SortByValue}
        sortDirValue={filterValues.sortDir}
        onSortByChange={(event: SelectChangeEvent) => onFilterChange({...filterValues, sortBy: event.target.value})}
        onSortDirChange={(sortDir: 'asc' | 'desc') => onFilterChange({...filterValues, sortDir})}
      />
    </SortFilterItemContainer>
  </SortFilterContainer>
};

export default SortFilter;
